import React, { FC, useEffect, useRef, useState, useCallback } from "react";
import { BlueLoadingIndicator } from "./LoadingBar.styles";

interface LoadingBarProps {
  isLoading: boolean;
  isCartLoading?: boolean;
}

const LoadingBar: FC<LoadingBarProps> = ({
  isLoading,
  isCartLoading = false,
}) => {
  const [loadPercentage, setLoadPercentage] = useState(0);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const wasActiveRef = useRef(false);

  // Derive active state from props
  const isActive = isLoading || isCartLoading;

  // Clear timeout helper
  const clearTimer = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = undefined;
    }
  }, []);

  // Handle loading state transitions and percentage progression
  useEffect(() => {
    clearTimer();

    // Detect transition to active state
    if (isActive && !wasActiveRef.current) {
      wasActiveRef.current = true;
      setLoadPercentage(30);
      return;
    }

    // Detect transition to inactive state
    if (!isActive && wasActiveRef.current) {
      wasActiveRef.current = false;
      setLoadPercentage(100);
      return;
    }

    // Progress while active
    if (isActive && loadPercentage > 0 && loadPercentage < 99) {
      const increment = loadPercentage > 80 ? 2 : loadPercentage > 60 ? 3 : 5;

      timeoutRef.current = setTimeout(() => {
        setLoadPercentage((prev) => Math.min(prev + increment, 99));
      }, 150);
    }

    // Reset after completion animation
    if (!isActive && loadPercentage === 100) {
      timeoutRef.current = setTimeout(() => {
        setLoadPercentage(0);
      }, 350);
    }

    return clearTimer;
  }, [isActive, loadPercentage, clearTimer]);

  // Cleanup on unmount
  useEffect(() => {
    return clearTimer;
  }, [clearTimer]);

  return (
    <BlueLoadingIndicator
      $isLoading={loadPercentage > 0}
      $percentage={loadPercentage}
    >
      <div />
    </BlueLoadingIndicator>
  );
};

export default LoadingBar;
